import React, {useState, useEffect, useRef} from "react";
import {useForm} from "react-hook-form";
import {Tags} from "lucide-react";
import {useParams, useNavigate, useLocation} from "react-router-dom";
import FormHeader from '../../Common/FormHeader';
import ActionMenu from '../../Common/ActionMenu';
import ColorPalette from "../ColorPalette";
import IOSSwitch from "../../Common/IOSSwitch";
import DiscountRulesModal from "./DiscountRulesModal";
import secureStorage from "../../../utils/secureStorage";
import {api} from "../../../config/api";
import {URLS_UPLOADS} from "../../../config/url";
import Notification from "../../Common/Notification";

// The API returns ISO datetimes (e.g. "2026-09-01T00:00:00+01:00") but
// <input type="date"> only accepts "YYYY-MM-DD", otherwise it renders empty.
const toDateInputValue = (value) => (value ? String(value).split('T')[0] : '');

const EditDiscountForm = () => {
    const {discountId} = useParams();
    const navigate = useNavigate();
    // Back to the catalogue list we came from (tab, filters and page kept)
    const returnTo = useLocation().state?.returnTo || "/catalogue";

    const [currentColor, setCurrentColor] = useState("#FFFF00");
    const [showPalette, setShowPalette] = useState(false);
    const [isActive, setIsActive] = useState(true);
    const [discountType, setDiscountType] = useState("%");
    const [isLoading, setIsLoading] = useState(true);
    const [isStockable, setIsStockable] = useState(true);
    const [isVendable, setIsVendable] = useState(true);
    const [maxDaysSinceLastVisit, setMaxDaysSinceLastVisit] = useState('');
    const [discountImage, setDiscountImage] = useState(null);
    const [clientTypeIds, setClientTypeIds] = useState([]);
    const [rulesStartDate, setRulesStartDate] = useState('');
    const [rulesEndDate, setRulesEndDate] = useState('');
    const [showRulesModal, setShowRulesModal] = useState(false);
    const [notification, setNotification] = useState({
        show: false,
        type: 'success',
        message: '',
    });

    const {
        register,
        handleSubmit,
        formState: {errors},
        setValue,
    } = useForm();

    const formRef = useRef(null);

    const showNotification = (type, message) => {
        setNotification({show: true, type, message});
        if (type === 'success') {
            setTimeout(() => setNotification(prev => ({...prev, show: false})), 5000);
        }
    };

    const hideNotification = () => {
        setNotification(prev => ({...prev, show: false}));
    };

    const getContrastColor = (hexColor) => {
        const r = parseInt(hexColor.slice(1, 3), 16);
        const g = parseInt(hexColor.slice(3, 5), 16);
        const b = parseInt(hexColor.slice(5, 7), 16);
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        return luminance > 0.5 ? '#000000' : '#FFFFFF';
    };

    useEffect(() => {
        const fetchDiscount = async () => {
            try {
                setIsLoading(true);
                const accountId = await secureStorage.getItem("selectedAccount");

                const response = await api.get(`/discounts/${discountId}`, {
                    headers: {
                        'X-account-id': accountId.id,
                        'Accept': 'application/json'
                    }
                });

                const discount = response.data;
                console.log("Fetched discount:", discount);

                setValue("name", discount.name);
                setValue("value", discount.value);
                setValue("description", discount.description || "");
                setClientTypeIds((discount.clientTypes || []).map(ct => ct.id));
                setRulesStartDate(toDateInputValue(discount.startDate));
                setRulesEndDate(toDateInputValue(discount.endDate));
                setCurrentColor(discount.color || "#FFFF00");
                setIsActive(discount.active === true);
                setDiscountType(discount.type || "%");
                // Default to true (applies to all products) for discounts created
                // before this option existed, so existing behavior is preserved.
                setIsStockable(discount.isStockable !== false);
                setIsVendable(discount.isVendable !== false);
                setMaxDaysSinceLastVisit(discount.maxDaysSinceLastVisit ?? '');

                if (discount.imageUrl) {
                    const imageUrl = discount.imageUrl.startsWith('http')
                        ? discount.imageUrl
                        : `${URLS_UPLOADS}${discount.imageUrl}`;
                    setDiscountImage(imageUrl);
                }

                setIsLoading(false);
            } catch (error) {
                console.error("Error fetching discount:", error);
                showNotification('error', "Impossible de récupérer les informations de la remise");
                setIsLoading(false);
            }
        };

        fetchDiscount();
    }, [discountId, setValue]);

    const onSubmit = async (data) => {
        try {
            showNotification('loading', "Mise à jour de la remise...");

            const valueAsNumber = parseFloat(data.value);
            if (isNaN(valueAsNumber)) {
                throw new Error("La valeur doit être un nombre valide");
            }

            const formData = new FormData();

            formData.append("id", discountId);
            formData.append("name", data.name);
            formData.append("value", valueAsNumber);
            formData.append("type", discountType);
            formData.append("active", String(isActive));
            formData.append("color", currentColor);
            formData.append("description", data.description || "");

            // Handle image upload
            if (discountImage instanceof File) {
                formData.append("image", discountImage);
            } else if (typeof discountImage === 'string' && discountImage.startsWith('data:')) {
                const response = await fetch(discountImage);
                const blob = await response.blob();
                const file = new File([blob], "discount-image.jpg", {type: "image/jpeg"});
                formData.append("image", file);
            }

            const accountId = await secureStorage.getItem("selectedAccount");

            // Log FormData for debugging
            for (let pair of formData.entries()) {
                console.log(pair[0] + ': ' + pair[1]);
            }

            const response = await api.post(`/discounts/${discountId}/update`, formData, {
                headers: {
                    'X-account-id': accountId.id,
                    'Accept': 'application/json',
                    'Content-Type': 'multipart/form-data'
                }
            });

            console.log("Discount updated:", response.data);
            showNotification('success', "Remise mise à jour avec succès");

            setTimeout(() => {
                navigate(returnTo);
            }, 1500);
        } catch (error) {
            console.error("Error updating discount:", error);
            const errorMessage = error.response?.data?.detail ||
                error.response?.data?.message ||
                error.message ||
                "Une erreur s'est produite";
            showNotification('error', `Erreur: ${errorMessage}`);
        }
    };

    const handleColorChange = (color) => {
        setCurrentColor(color);
        setShowPalette(false);
    };

    const handleImageChange = (image) => {
        setDiscountImage(image);
    };

    const togglePalette = () => {
        setShowPalette((prev) => !prev);
    };

    const toggleActive = () => {
        setIsActive((prev) => !prev);
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-white dark:bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-solid border-primary border-r-transparent align-[-0.125em]" role="status">
                        <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">Loading...</span>
                    </div>
                    <p className="mt-2 text-gray-600 dark:text-gray-300">Chargement de la remise...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white dark:bg-gray-900">
            <FormHeader title="Modifier la remise" back={returnTo} onSave={() => formRef.current && handleSubmit(onSubmit)()}>
                <ActionMenu
                    label="Plus d'actions"
                    items={[{label: 'Gérer les règles', icon: Tags, onClick: () => setShowRulesModal(true)}]}
                    trigger={{variant: 'plain'}}
                />
            </FormHeader>

            <form
                ref={formRef}
                className="max-w-3xl mx-auto px-4 sm:px-6 md:px-8 py-6 space-y-6 w-full"
                onSubmit={handleSubmit(onSubmit)}
            >
                <div className="flex flex-col items-center mb-6">
                    <div
                        onClick={togglePalette}
                        style={{backgroundColor: currentColor, color: getContrastColor(currentColor)}}
                        className="w-24 h-24 rounded-lg cursor-pointer flex items-center justify-center shadow-md overflow-hidden"
                    >
                        {discountImage ? (
                            <img
                                src={typeof discountImage === 'string'
                                    ? discountImage
                                    : URL.createObjectURL(discountImage)
                                }
                                alt="Remise"
                                className="w-full h-full object-cover"
                            />
                        ) : (
                            <span className="text-sm font-medium">Éditer</span>
                        )}
                    </div>
                    <p className="text-gray-500 text-xs mt-2">Cliquez pour modifier</p>
                </div>

                <div className="space-y-6">
                    <div>
                        <label htmlFor="name" className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                            Nom :
                        </label>
                        <input
                            type="text"
                            id="name"
                            autoComplete="off"
                            {...register("name", {
                                required: "Le champ 'Nom' est obligatoire.",
                            })}
                            placeholder="Nom de la remise*"
                            className={`w-full px-3 py-2 text-sm border dark:bg-gray-800 dark:text-white dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 ${
                                errors.name
                                    ? "border-red-500 focus:ring-red-500 dark:border-red-700"
                                    : "border-gray-300 focus:ring-primary focus:border-primary dark:focus:border-primary"
                            }`}
                        />
                        {errors.name && (
                            <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>
                        )}
                    </div>

                    <div>
                        <label htmlFor="value" className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                            Valeur :
                        </label>
                        <div className="relative">
                            <input
                                type="number"
                                id="value"
                                step="0.01"
                                {...register("value", {
                                    required: "Le champ 'Valeur' est obligatoire.",
                                    min: {
                                        value: 0,
                                        message: "La valeur doit être positive."
                                    },
                                })}
                                placeholder="Valeur*"
                                className={`w-full px-3 py-2 text-sm border dark:bg-gray-800 dark:text-white dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 ${
                                    errors.value
                                        ? "border-red-500 focus:ring-red-500 dark:border-red-700"
                                        : "border-gray-300 focus:ring-primary focus:border-primary dark:focus:border-primary"
                                }`}
                            />
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                <span className="text-gray-500 text-sm">{discountType}</span>
                            </div>
                        </div>
                        {errors.value && (
                            <p className="text-red-500 text-xs mt-1">{errors.value.message}</p>
                        )}
                    </div>

                    <div>
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                            Type de remise :
                        </label>
                        <div className="inline-flex rounded-md border border-gray-300 overflow-hidden">
                            <button
                                type="button"
                                onClick={() => setDiscountType("DH")}
                                className={`px-6 py-2 flex items-center justify-center text-sm ${
                                    discountType === "DH"
                                        ? "bg-primary text-white"
                                        : "bg-white text-gray-700 hover:bg-gray-50"
                                }`}
                            >
                                <span>DH</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => setDiscountType("%")}
                                className={`px-6 py-2 flex items-center justify-center text-sm ${
                                    discountType === "%"
                                        ? "bg-primary text-white"
                                        : "bg-white text-gray-700 hover:bg-gray-50"
                                }`}
                            >
                                <span>%</span>
                            </button>
                        </div>
                    </div>

                    <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                        <div>
                            <label htmlFor="description" className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                                Description (optionnelle) :
                            </label>
                            <textarea
                                id="description"
                                {...register("description")}
                                rows="3"
                                placeholder="Description de la remise"
                                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-800
                                dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                            />
                        </div>
                    </div>

                    <div className="flex items-center">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300 mr-3">Actif :</span>
                        <IOSSwitch
                            checked={isActive}
                            onChange={toggleActive}
                        />
                    </div>
                </div>
            </form>

            {showPalette && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="w-full max-w-2xl bg-white dark:bg-gray-800 rounded-lg shadow-xl mx-4">
                        <ColorPalette
                            initialColor={currentColor}
                            onClose={togglePalette}
                            onColorChange={handleColorChange}
                            onImageChange={handleImageChange}
                            currentImage={discountImage}
                        />
                    </div>
                </div>
            )}

            {showRulesModal && (
                <DiscountRulesModal
                    discountId={discountId}
                    initialClientTypeIds={clientTypeIds}
                    initialStartDate={rulesStartDate}
                    initialEndDate={rulesEndDate}
                    initialIsStockable={isStockable}
                    initialIsVendable={isVendable}
                    initialMaxDaysSinceLastVisit={maxDaysSinceLastVisit}
                    onClose={() => setShowRulesModal(false)}
                    showNotification={showNotification}
                    onSaved={({clientTypeIds: ids, startDate, endDate, isStockable: stockable, isVendable: vendable, maxDaysSinceLastVisit: maxDays}) => {
                        setClientTypeIds(ids);
                        setRulesStartDate(toDateInputValue(startDate));
                        setRulesEndDate(toDateInputValue(endDate));
                        setIsStockable(stockable !== false);
                        setIsVendable(vendable !== false);
                        setMaxDaysSinceLastVisit(maxDays ?? '');
                    }}
                />
            )}

            <Notification
                show={notification.show}
                type={notification.type}
                message={notification.message}
                onClose={hideNotification}
            />
        </div>
    );
};

export default EditDiscountForm;
